import styled from "@emotion/styled"
import { DealerContextProvider } from "@porsche-customer/slfinrtl-context"
import type { DealerContextProps, Locale } from "@porsche-customer/slfinrtl-context-types"
import { PBanner, PInlineNotification, PSpinner } from "@porsche-design-system/components-react"
// eslint-disable-next-line no-restricted-imports
import { generateReferralLinkRoute } from "@slfinrtl/ui-bff-routes"
import type { PropsWithChildren } from "react"
import { Suspense, useCallback, useContext, useEffect, useMemo, useState } from "react"
import { RouterProvider } from "react-router"
import { callBff } from "./common/callBff"
import { CognitoErrorBanner, ContentWrapper } from "./components"
import { AdobeAnalytics } from "./components/adobeAnalytics"
import { FeedbackFlyoutContainer } from "./containers/feedback/FeedbackFlyoutContainer"
import { ErrorBannerProvider } from "./context/errorBanner/ErrorBannerProvider"
import { TranslationsContext } from "./context/translations/TranslationsContext"
import { TranslationsProvider } from "./context/translations/TranslationsProvider"
import { useFetchDealerContext } from "./hooks/useFetchDealerContext/useFetchDealerContext"
import type { DealerContextResult, FetchDealerContextError } from "./hooks/useFetchDealerContext/useFetchDealerContext"
import { useDealerAppRouter } from "./useDealerAppRouter"

interface DealerAppRouterProps {
	dealershipId: string
	locale?: Locale
}

export const DealerAppRouter = ({ dealershipId, locale }: DealerAppRouterProps) => {
	const [context, setContext] = useState<DealerContextResult>()
	const [contextError, setContextError] = useState<string>()
	const [hasCognitoError, setHasCognitoError] = useState(false)
	const [isErrorBannerDismissed, setIsErrorBannerDismissed] = useState(false)
	const [referralId, setReferralId] = useState<string>()

	const handleCognitoError = useCallback(() => setHasCognitoError(true), [])
	const fetchContext = useFetchDealerContext(dealershipId, locale, handleCognitoError)

	const dismissErrorBanner = useCallback(() => setIsErrorBannerDismissed(true), [])

	useEffect(() => {
		fetchContext()
			.then(setContext)
			.catch((error: FetchDealerContextError) => {
				if (error.redirectUrl) {
					window.location.replace(error.redirectUrl)
				}
				if (error.message) {
					setContextError(error.message)
				}
			})
	}, [fetchContext])

	const getReferralId = useCallback(() => {
		if (!context) {
			return Promise.reject(new Error("Dealer context not loaded"))
		}
		if (referralId) {
			return Promise.resolve(referralId)
		}
		return callBff(context, generateReferralLinkRoute, { body: undefined, pathParams: {}, queryParams: {} }).then(
			(referralLinkView) => {
				setReferralId(referralLinkView.referralId)
				return referralLinkView.referralId
			},
		)
	}, [referralId, context])

	const dealerContext = useMemo<DealerContextProps | undefined>(() => {
		return context ? { ...context, getReferralId } : undefined
	}, [context, getReferralId])

	if (contextError) {
		return (
			<PBanner
				state="error"
				heading="There was an error requesting the dealer context"
				open
				dismissButton={false}
			>
				{contextError}
			</PBanner>
		)
	}

	return context ? (
		<TranslationsProvider locale={context.locale || "en"}>
			<TranslationsWrapper>
				<DealerContextProvider context={dealerContext!}>
					<AdobeAnalytics dealerContext={dealerContext} />
					<ErrorBannerProvider>
						<Suspense fallback={null}>
							{hasCognitoError && <CognitoErrorBanner />}
							{context.errors.length > 0 && (
								<PBanner open={!isErrorBannerDismissed} onDismiss={dismissErrorBanner} state="error">
									{context.errors.map((error, index) => (
										<div key={index}>{error}</div>
									))}
								</PBanner>
							)}
							<DealerAppRoutes dealershipId={dealershipId} locale={locale} />
							<FeedbackFlyoutContainer />
						</Suspense>
					</ErrorBannerProvider>
				</DealerContextProvider>
			</TranslationsWrapper>
		</TranslationsProvider>
	) : (
		<CenteredSpinner />
	)
}

const DealerAppRoutes = ({ dealershipId, locale }: DealerAppRouterProps) => {
	const router = useDealerAppRouter(dealershipId, locale ?? "de")
	return router ? (
		<RouterProvider router={router} />
	) : (
		<ContentWrapper>
			<PInlineNotification
				state="error"
				dismissButton={false}
				heading="Missing permissions"
				description="You don't have permission to access the Finder dealer application. Please contact your PPN Administrator"
			/>
		</ContentWrapper>
	)
}

const TranslationsWrapper = ({ children }: PropsWithChildren) => {
	const { isLoaded } = useContext(TranslationsContext)
	return isLoaded ? children : <CenteredSpinner />
}

const CenteredSpinner = styled(PSpinner)`
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate3d(-50%, -50%, 0);
`
